import { useMedia } from 'helpers/hooks/useMedia'
import { StreamSectionTrackingContextProvider } from 'helpers/tracking/SectionTrackingContext'
import { useActiveSortChannel } from 'helpers/tracking/hooks/useActiveSortChannel'
import IdolStoryOutfitHeader, {
  calcHeight as idolStoryOutfitHeaderCalcHeight,
} from 'pages/Story/components/IdolStoryOutfitHeader'
import React, { CSSProperties, FC } from 'react'
import { IService } from 'services'
import {
  CommonStoryRow,
  ElementRow,
  NormalizedStoryElement,
  NormalizedStoryOutfitElement,
  NormalizedStoryTextElement,
  StoryRow,
  TileRatio,
} from 'services/providers/iris/entities/stories/storyRows/storyRow.normalizer.types'
import { useStoryPageStore, useUrlManagerStore } from 'store/context'
import styled from 'styled-components'
import {
  mediaDesktopAndUp,
  mediaExtraLargeScreens,
  mediaTabletAndDown,
} from 'uiComponents/constants/customMedia'
import DoubleTile, {
  EVEN_TILE_WIDTH,
  SHIFTED_PADDING,
  SMALL_TILE_WIDTH,
  SPACE_BETWEEN_TILES,
} from 'uiComponents/molecules/DoubleTile/DoubleTile'
import { calcHeight as editorialHeadlineCalcHeight } from 'uiComponents/molecules/EditorialHeadline/EditorialHeadline'
import SingleTile from 'uiComponents/molecules/SingleTile/SingleTile'
import {
  CalcHeightConfig,
  FCRow,
} from 'uiComponents/molecules/TileGrid/TileGrid'
import { calcHeight as editorialOutfitRecoCalcHeight } from 'uiComponents/organisms/EditorialOutfitReco/EditorialOutfitReco'
import { ContentNode, elementToContent } from './editorialRowHelpers'

const CONTAINER_PADDING = 90
const CONTAINER_PADDING_IDOL_STORY_OUTFIT_HEADER = 30

const SINGLE_TILE_WIDTH = 2 / 3
const SINGLE_TILE_LANDSCAPE_WIDTH = 1

export const DEFAULT_IMAGE_SIZE = 1560
// Roughly measured: regular story / full width
const IMAGE_SIZE_MAPPING = {
  [mediaTabletAndDown]: 780,
  [mediaDesktopAndUp]: 1008,
  [mediaExtraLargeScreens]: 1500,
}

// Roughly measured: regular story / full width
// Half-width on larger screens
const DOUBLE_TILE_IMAGE_SIZE_MAPPING = {
  [mediaTabletAndDown]: 780,
  [mediaDesktopAndUp]: 504,
  [mediaExtraLargeScreens]: 780,
}

const EditorialSingleTile = styled(SingleTile)`
  width: ${props =>
    props.ratio === '4:3'
      ? `${SINGLE_TILE_LANDSCAPE_WIDTH * 100}%`
      : `${SINGLE_TILE_WIDTH * 100}%`};

  @media ${mediaTabletAndDown} {
    width: 100%;
  }
`

interface ContainerProps {
  isOutfitHeader: boolean
}

const EditorialRowContainer = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: center;

  padding-bottom: ${({ isOutfitHeader }) =>
    isOutfitHeader
      ? CONTAINER_PADDING_IDOL_STORY_OUTFIT_HEADER
      : CONTAINER_PADDING}px;
  position: relative;
`

type EditorialRowProps = StoryRow & {
  rowIndex: number
  rowIndexOffset: number
  style?: CSSProperties
}

const EditorialSingleElement: FC<{
  content: ContentNode
  ratio: CommonStoryRow['tileRatio']
}> = ({ content, ratio }) => {
  if (content.type === 'image' || content.type === 'video') {
    return (
      <EditorialSingleTile ratio={ratio || '1:1'}>
        {content.node}
      </EditorialSingleTile>
    )
  }

  return <>{content.node}</>
}

const EditorialRow: FCRow<EditorialRowProps> = ({
  style,
  rowIndex,
  rowIndexOffset,
  ...storyRow
}) => {
  const urlManagerStore = useUrlManagerStore()
  const sortChannel = useActiveSortChannel()
  const environment = urlManagerStore.environment
  const isOutfitHeader = storyRow.type === 'idol-story-outfit-header'
  const singleTileContentConfig = {
    imgSize: useMedia(IMAGE_SIZE_MAPPING, DEFAULT_IMAGE_SIZE),
  }
  const doubleTileContentConfig = {
    imgSize: useMedia(DOUBLE_TILE_IMAGE_SIZE_MAPPING, DEFAULT_IMAGE_SIZE),
  }
  const { seo } = useStoryPageStore()

  return (
    <StreamSectionTrackingContextProvider
      sectionIndexOnPage={rowIndex + rowIndexOffset}
      sortChannel={sortChannel}
      recoType="product_stream"
    >
      <EditorialRowContainer
        isOutfitHeader={isOutfitHeader}
        data-testid="EditorialRowContainer"
        style={style}
      >
        {storyRow.type === 'idol-story-outfit-header' ? (
          <IdolStoryOutfitHeader
            idolName={storyRow.idolName}
            outfitCount={storyRow.outfitCount}
          />
        ) : (
          storyRow.elementRows.map((elements, index) => {
            if (elements.length > 1) {
              const [start, end] = elements as [
                NormalizedStoryElement,
                NormalizedStoryElement,
              ]
              return (
                <DoubleTile
                  key={index}
                  ratio={storyRow.tileRatio}
                  shift={storyRow.shifted}
                  contentStart={elementToContent({
                    kind: 'doubleTile',
                    element: start,
                    environment,
                    config: doubleTileContentConfig,
                    seo,
                  })}
                  contentEnd={elementToContent({
                    kind: 'doubleTile',
                    element: end,
                    environment,
                    config: doubleTileContentConfig,
                    seo,
                  })}
                />
              )
            } else {
              return (
                <EditorialSingleElement
                  key={index}
                  ratio={storyRow.tileRatio}
                  content={elementToContent({
                    kind: 'singleTile',
                    element: elements[0],
                    environment,
                    config: singleTileContentConfig,
                    seo,
                  })}
                />
              )
            }
          })
        )}
      </EditorialRowContainer>
    </StreamSectionTrackingContextProvider>
  )
}

const tileRatioToHeightFactor: { [key in TileRatio]: number } = {
  // height factor is the inverse of the ratio, which is describing the width factor
  '1:1': 1,
  '4:3': 3 / 4,
  '3:4': 4 / 3,
}

EditorialRow.calcHeight = ({
  service,
  clientWidth,
  listWidth,
  rowProps,
}: CalcHeightConfig<EditorialRowProps>) => {
  if (!rowProps) {
    return CONTAINER_PADDING
  }

  const { style, componentType, rowIndex, ...storyRow } = rowProps

  if (storyRow.type === 'idol-story-outfit-header') {
    return (
      CONTAINER_PADDING_IDOL_STORY_OUTFIT_HEADER +
      idolStoryOutfitHeaderCalcHeight()
    )
  }

  const isExtraSmallScreen = process.browser
    ? window.matchMedia(mediaTabletAndDown).matches
    : false

  const singleTileWidth = isExtraSmallScreen ? 1 : SINGLE_TILE_WIDTH

  let rowHeight = storyRow.elementRows.reduce((summedHeight, elements) => {
    const rowContainsText = elements.some(element => element.type === 'text')

    const imageSideFactor = rowContainsText
      ? 1 - SMALL_TILE_WIDTH
      : EVEN_TILE_WIDTH
    const splitLayout = elements.length > 1

    let ratioFactor = tileRatioToHeightFactor[storyRow.tileRatio || '1:1']

    if (!splitLayout && storyRow.tileRatio !== '4:3') {
      ratioFactor = ratioFactor * singleTileWidth
    }

    return (
      summedHeight +
      elements.reduce(
        getMaxElementHeight({
          service,
          clientWidth,
          imageSideFactor,
          listWidth,
          ratioFactor,
          splitLayout,
          isExtraSmallScreen,
          rowContainsText,
        }),
        0,
      )
    )
  }, 0)

  if (storyRow.shifted) {
    rowHeight += SHIFTED_PADDING * 2
  }

  return rowHeight > 0 ? rowHeight + CONTAINER_PADDING : 0
}

interface RowHeightConfig {
  service: IService
  splitLayout: boolean
  listWidth: number
  clientWidth: number
  imageSideFactor: number
  ratioFactor: number
  isExtraSmallScreen: boolean
  rowContainsText: boolean
}

const calcTextTileHeight =
  ({
    splitLayout,
    listWidth,
    clientWidth,
    isExtraSmallScreen,
  }: RowHeightConfig) =>
  (element: NormalizedStoryTextElement) => {
    const elementWidth =
      splitLayout && !isExtraSmallScreen
        ? (listWidth - SPACE_BETWEEN_TILES) * SMALL_TILE_WIDTH
        : listWidth

    return editorialHeadlineCalcHeight({
      clientWidth,
      listWidth: elementWidth,
      title: element.title,
      text: element.text,
    })
  }

const calcOutfitTileHeight = (
  config: RowHeightConfig,
  element: NormalizedStoryOutfitElement,
) => {
  return editorialOutfitRecoCalcHeight({
    service: config.service,
    listWidth: config.listWidth,
    isExtraSmallScreen: config.isExtraSmallScreen,
    outfitId: element.outfitId,
  })
}

const getMaxElementHeight =
  (config: RowHeightConfig) =>
  (maxHeight: number, element: ElementRow[number]): number => {
    let elementHeight = 0

    switch (element.type) {
      case 'text': {
        elementHeight = calcTextTileHeight(config)(element)
        break
      }
      case 'video':
      case 'image': {
        const {
          splitLayout,
          listWidth,
          ratioFactor,
          imageSideFactor,
          isExtraSmallScreen,
        } = config
        const elementWidth =
          splitLayout && !isExtraSmallScreen
            ? (listWidth - SPACE_BETWEEN_TILES) * imageSideFactor
            : listWidth

        elementHeight = ratioFactor * elementWidth

        break
      }
      case 'outfit': {
        elementHeight = calcOutfitTileHeight(config, element)
        break
      }
    }

    return config.isExtraSmallScreen
      ? maxHeight + elementHeight
      : Math.max(maxHeight, elementHeight)
  }

export default EditorialRow
